import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import FacebookRoundedIcon from '@mui/icons-material/FacebookRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ShareRoundedIcon from '@mui/icons-material/ShareRounded';
import CardGiftcardRoundedIcon from '@mui/icons-material/CardGiftcardRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import { triguardBrandProfile } from '../../branding/companyBrand';
import BrandedPreviewShell from '../previews/BrandedPreviewShell';
import { CXReferral, getCXReferralAccess } from '../../services/cxReferralService';

const fallbackReferralLink = `${window.location.origin}/r/triguard-friend`;

const ReferralDashboardPreview: React.FC = () => {
  const [copied, setCopied] = useState(false);
  const [liveReferral, setLiveReferral] = useState<CXReferral | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('cxReferralAccessToken');
    if (token) getCXReferralAccess(token).then(setLiveReferral).catch(() => setLiveReferral(null));
  }, []);

  const referralLink = liveReferral ? `${window.location.origin}/r/${liveReferral.referralCode}` : fallbackReferralLink;
  const stats = [
    { label: 'Referrals', value: liveReferral ? '1' : '18' },
    { label: 'Booked', value: ['booked', 'qualified', 'paid'].includes(liveReferral?.status ?? '') ? '1' : '6' },
    { label: 'Earned', value: ['qualified', 'paid'].includes(liveReferral?.status ?? '') ? '$50' : '$300' },
  ];

  const handleCopy = async () => {
    try {
      if (!navigator.clipboard) throw new Error('Clipboard API unavailable');
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const handleShare = async (destination: 'facebook' | 'whatsapp' | 'share') => {
    if (destination === 'facebook') {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`, '_blank', 'noopener,noreferrer');
      return;
    }
    if (destination === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodeURIComponent(`I thought you might like ${triguardBrandProfile.companyName}. ${referralLink}`)}`, '_blank', 'noopener,noreferrer');
      return;
    }
    if (navigator.share) {
      await navigator.share({ title: `${triguardBrandProfile.companyName} referral`, url: referralLink });
      return;
    }
    await handleCopy();
  };

  return (
    <BrandedPreviewShell brand={triguardBrandProfile} previewLabel="Referral dashboard" statusLabel="Rewards">
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Typography variant="h4" sx={{ color: 'var(--cx-brand-text)', fontSize: { xs: 28, sm: 32 }, lineHeight: 1.15 }}>Your referral rewards</Typography>
          <Typography variant="body2" sx={{ color: 'var(--cx-brand-text-muted)' }}>Give neighbors $50 off and earn $50 after their first paid service.</Typography>
        </Stack>

        <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
          {stats.map((stat) => (
            <Card key={stat.label} sx={{ p: 1.5 }}>
              <Typography sx={{ color: 'var(--cx-brand-text)', fontSize: { xs: 23, sm: 26 }, fontWeight: 900, lineHeight: 1.1 }}>{stat.value}</Typography>
              <Typography variant="caption" sx={{ color: 'var(--cx-brand-text-muted)', fontSize: 11, fontWeight: 700 }}>{stat.label}</Typography>
            </Card>
          ))}
        </Box>

        <Card sx={{ background: 'linear-gradient(155deg, var(--cx-brand-primary-dark) 0%, #103F46 100%)', color: 'var(--color-base-white)', p: 2.5 }}>
          <Stack spacing={2} sx={{ alignItems: 'center', textAlign: 'center' }}>
            <Box sx={{ alignItems: 'center', bgcolor: 'rgba(255,255,255,0.12)', borderRadius: 2, display: 'flex', height: 48, justifyContent: 'center', width: 48 }}>
              <CardGiftcardRoundedIcon />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ color: 'inherit', fontWeight: 900 }}>Share your referral</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.72)', mt: 0.5 }}>Choose how you want to send it.</Typography>
            </Box>
            <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} sx={{ justifyContent: 'center' }}>
              {[
                { label: copied ? 'Copied' : 'Copy link', icon: ContentCopyRoundedIcon, action: handleCopy },
                { label: 'Share on Facebook', icon: FacebookRoundedIcon, action: () => handleShare('facebook') },
                { label: 'Share on WhatsApp', icon: WhatsAppIcon, action: () => handleShare('whatsapp') },
                { label: 'Share referral', icon: ShareRoundedIcon, action: () => handleShare('share') },
              ].map(({ action, icon: Icon, label }) => (
                <IconButton
                  aria-label={label}
                  key={label}
                  onClick={() => void action().catch(() => undefined)}
                  sx={{ bgcolor: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.55)', color: 'var(--color-base-white)', height: 52, width: 52, '&:hover': { bgcolor: 'rgba(255,255,255,0.26)' } }}
                >
                  <Icon />
                </IconButton>
              ))}
            </Stack>
          </Stack>
        </Card>

        <Card sx={{ background: 'linear-gradient(145deg, var(--cx-brand-primary-dark) 0%, #174A50 100%)', color: 'var(--color-base-white)', overflow: 'hidden', p: 2.25, position: 'relative' }}>
          <Box sx={{ bgcolor: 'rgba(255,255,255,0.06)', borderRadius: '50%', height: 190, position: 'absolute', right: -72, top: -88, width: 190 }} />
          <Stack spacing={2} sx={{ position: 'relative' }}>
            <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                <AccountBalanceWalletRoundedIcon sx={{ fontSize: 21 }} />
                <Typography variant="subtitle2" sx={{ color: 'inherit' }}>Rewards wallet</Typography>
              </Box>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 800 }}>Powered by Dots</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.68)', fontWeight: 700 }}>Available balance</Typography>
              <Typography sx={{ fontSize: 38, fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1.15 }}>$150.00</Typography>
            </Box>
            <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.72)' }}>Pending $50.00</Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.58)' }}>Rewards wallet</Typography>
            </Box>
          </Stack>
        </Card>

        <Button component={RouterLink} fullWidth endIcon={<LaunchRoundedIcon />} startIcon={<HistoryRoundedIcon />} sx={{ borderColor: 'var(--cx-brand-border)', color: 'var(--cx-brand-text)', fontWeight: 800 }} to="/preview/referral-activity" variant="outlined">View referral activity</Button>
      </Stack>
    </BrandedPreviewShell>
  );
};

export default ReferralDashboardPreview;
